/**
 * 6-digit code input as 6 single-character cells. Auto-advances on input,
 * back-tabs on backspace, supports paste (digits are spread across cells).
 *
 *   const code = createCodeInput(container, { onComplete: (value) => {...} });
 *   code.value();   // current concatenated string
 *   code.focus();   // focus the first empty cell
 *   code.clear();   // wipe + focus first cell
 */
export function createCodeInput(host, { length = 6, onComplete } = {}) {
  host.classList.add('code-input');
  host.innerHTML = Array.from({ length })
    .map(
      (_, i) =>
        `<input
          class="code-cell"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="1"
          aria-label="digit ${i + 1}"
          data-index="${i}"
        />`,
    )
    .join('');

  const cells = Array.from(host.querySelectorAll('.code-cell'));

  function value() {
    return cells.map((c) => c.value).join('');
  }
  function focusFirstEmpty() {
    const empty = cells.find((c) => !c.value) || cells[cells.length - 1];
    empty.focus();
    empty.select?.();
  }
  function clear() {
    cells.forEach((c) => (c.value = ''));
    cells[0].focus();
  }
  function setFrom(str) {
    const digits = (str || '').replace(/\D/g, '').slice(0, length).split('');
    cells.forEach((c, i) => (c.value = digits[i] || ''));
    focusFirstEmpty();
    if (value().length === length) onComplete?.(value());
  }

  cells.forEach((cell, i) => {
    cell.addEventListener('input', (e) => {
      // Strip anything non-numeric and keep just the last typed char.
      const raw = e.target.value.replace(/\D/g, '');
      e.target.value = raw.slice(-1);
      if (raw.length > 1) {
        // Likely an autofill-style paste landing in one cell — distribute.
        setFrom(value().slice(0, i) + raw + value().slice(i + 1));
        return;
      }
      if (e.target.value && i < cells.length - 1) cells[i + 1].focus();
      if (value().length === length) onComplete?.(value());
    });

    cell.addEventListener('keydown', (e) => {
      if (e.key === 'Backspace' && !cell.value && i > 0) {
        cells[i - 1].focus();
        cells[i - 1].value = '';
      } else if (e.key === 'ArrowLeft' && i > 0) {
        cells[i - 1].focus();
      } else if (e.key === 'ArrowRight' && i < cells.length - 1) {
        cells[i + 1].focus();
      }
    });

    cell.addEventListener('paste', (e) => {
      e.preventDefault();
      setFrom(e.clipboardData?.getData('text') || '');
    });
  });

  return { value, focus: focusFirstEmpty, clear, setFrom };
}
